import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

const LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function HoursEditor() {
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get(`${API}/admin/hours`)
      .then((r) => setDays(r.data))
      .catch(() => toast.error("Erro ao carregar horários."));
  }, []);

  const setDay = (i, patch) => setDays((p) => ({ ...p, [i]: { ...p[i], ...patch } }));

  const setWindow = (i, wi, pos, value) =>
    setDays((p) => {
      const windows = p[i].windows.map((w, idx) => (idx === wi ? (pos === 0 ? [value, w[1]] : [w[0], value]) : w));
      return { ...p, [i]: { ...p[i], windows } };
    });

  const addWindow = (i) =>
    setDays((p) => ({ ...p, [i]: { ...p[i], windows: [...p[i].windows, ["14:00", "19:30"]].slice(0, 2) } }));

  const removeWindow = (i, wi) =>
    setDays((p) => ({ ...p, [i]: { ...p[i], windows: p[i].windows.filter((_, idx) => idx !== wi) } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await http.put(`${API}/admin/hours`, { days });
      setDays(data);
      toast.success("Horários salvos. A agenda já respeita os novos horários.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!days) {
    return <p className="py-16 text-center text-sm text-smoke" data-testid="hours-loading">Carregando...</p>;
  }

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="hours-editor">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-bebas text-4xl tracking-wide text-cream">Horário de funcionamento</h2>
          <p className="text-sm text-smoke mt-1">Até 2 janelas por dia (ex.: manhã e tarde). Dias fechados não aceitam agendamento.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="hours-save-button"
          className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-lg tracking-widest px-6 py-2.5 hover:bg-golddark transition-colors disabled:opacity-40"
        >
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {LABELS.map((label, i) => {
          const day = days[String(i)] || { closed: true, windows: [] };
          return (
            <div
              key={label}
              data-testid={`hours-row-${i}`}
              className={`flex flex-wrap items-center gap-4 border p-4 transition-colors ${day.closed ? "border-white/5 bg-surface/40" : "border-white/10 bg-surface"}`}
            >
              <span className={`font-bebas text-2xl tracking-widest w-32 ${day.closed ? "text-smoke" : "text-cream"}`}>{label}</span>
              <button
                onClick={() => setDay(i, { closed: !day.closed })}
                data-testid={`hours-closed-${i}`}
                className={`focus-gold font-bebas tracking-widest text-sm px-4 py-2 border transition-colors ${day.closed ? "border-eraorange text-eraorange" : "border-eragreen text-green-400"}`}
              >
                {day.closed ? "Fechado" : "Aberto"}
              </button>
              {!day.closed && (
                <>
                  {day.windows.map((w, wi) => (
                    <div key={wi} className="flex items-center gap-2" data-testid={`hours-window-${i}-${wi}`}>
                      <input
                        type="time"
                        value={w[0]}
                        onChange={(e) => setWindow(i, wi, 0, e.target.value)}
                        aria-label={`${label} janela ${wi + 1} início`}
                        data-testid={`hours-start-${i}-${wi}`}
                        className="focus-gold bg-ink border border-white/15 text-cream font-manrope px-3 py-2 text-sm outline-none [color-scheme:dark]"
                      />
                      <span className="text-smoke text-xs">até</span>
                      <input
                        type="time"
                        value={w[1]}
                        onChange={(e) => setWindow(i, wi, 1, e.target.value)}
                        aria-label={`${label} janela ${wi + 1} fim`}
                        data-testid={`hours-end-${i}-${wi}`}
                        className="focus-gold bg-ink border border-white/15 text-cream font-manrope px-3 py-2 text-sm outline-none [color-scheme:dark]"
                      />
                      {day.windows.length > 1 && (
                        <button
                          onClick={() => removeWindow(i, wi)}
                          aria-label={`Remover janela ${wi + 1} de ${label}`}
                          data-testid={`hours-remove-window-${i}-${wi}`}
                          className="focus-gold p-2 border border-white/15 text-smoke hover:border-red-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {day.windows.length < 2 && (
                    <button
                      onClick={() => addWindow(i)}
                      data-testid={`hours-add-window-${i}`}
                      className="focus-gold inline-flex items-center gap-1.5 border border-white/15 text-smoke font-bebas tracking-widest text-sm px-3 py-2 hover:border-gold hover:text-gold transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Janela
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-xs text-smoke">Lembre-se de clicar em <span className="text-gold">Salvar</span> para aplicar as mudanças no site.</p>
    </main>
  );
}
